import React, { useState, useEffect } from 'react';
import { connect } from '../../../redux/store';
import {
  actions,
  selectIsAppendixOwner,
} from '../../../redux/modules/settingsAppendix';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import {
  Card,
  CardCol,
  CardRow,
  Countdown,
  ErrorMessage,
  LoadingMessage,
  TimeAgo,
  C,
  addToast,
  addToastAlert,
  openConfirm,
} from '@kineticdata/bundle-common';
import { getCatalogRevisionLockData } from '../../../utils';
import { PageTitle } from '../../shared/PageTitle';
import { ContentManager } from '../../shared/content_manager/ContentManager';
import { LockController } from './LockController';
import {
  CONTENT_MANAGER_MODE,
  getStatusColor,
} from '../../shared/content_manager/helpers';
import { List, Map } from 'immutable';

const AppendixRevisionComponent = ({
  portfolioId,
  serviceId,
  appendixId,
  revisionId,
  mode,
  // Selected revision
  revision,
  revisionError,
  revisionLoading,
  // Are user actions pending
  actionsPending,
  // Current appendix
  appendix,
  appendixError,
  appendixLoading,
  // All portfolios and services
  portfoliosNameMap,
  servicesNameMap,
  // Actions
  fetchAppendixRevision,
  saveAppendixRevision,
  deleteAppendixRevision,
  submitAppendixRevision,
  lockAppendixRevision,
  resetAppendixRevision,
  // Other props
  navigate,
  profile,
  isOwner,
  appLocation,
  isMobile,
}) => {
  // Fetch the revision record when revisionId or appendixId changes
  useEffect(
    () => {
      if (revisionId) {
        fetchAppendixRevision(revisionId, appendixId);
      }
      return () => {
        resetAppendixRevision(revisionId);
      };
    },
    [fetchAppendixRevision, resetAppendixRevision, revisionId, appendixId],
  );

  // State for tracking if the content manager is dirty
  const [isDirty, setIsDirty] = useState(false);

  const isRevisionDraft =
    !!revision && revision.values[C.FIELD_STATUS] === 'Draft';
  const isRevisionCompleted =
    !isRevisionDraft &&
    !!revision &&
    revision.values[C.FIELD_STATUS] !== 'Pending';
  const isAppendixDraft =
    !!appendix &&
    appendix.values &&
    appendix.values[C.FIELD_STATUS] === 'Draft';
  const doesRevisionHaveChildren =
    !!revision && revision.children && revision.children.length > 0;
  const pathToRevision = `..${!!mode ? '/..' : ''}`;
  const pathToAppendix = `../${pathToRevision}`;
  const pathToAppendices = `../${pathToAppendix}`;

  const contentMode =
    !revision || !isRevisionDraft || !isOwner
      ? CONTENT_MANAGER_MODE.REVIEW
      : !!mode
        ? CONTENT_MANAGER_MODE.SUBMIT
        : CONTENT_MANAGER_MODE.EDIT;

  // Lock info
  const lockingEnabled =
    contentMode === CONTENT_MANAGER_MODE.EDIT ||
    contentMode === CONTENT_MANAGER_MODE.SUBMIT;
  const { isLocked, isLockedByMe, lockTimeLeft } = getCatalogRevisionLockData(
    revision,
    profile.username,
  );
  // State and effect for keeping track of whether this submission has been
  // locked by me at any point.
  const [hasBeenLockedByMe, setHasBeenLockedByMe] = useState(!!isLockedByMe);
  useEffect(
    () => {
      if (!!isLockedByMe) setHasBeenLockedByMe(true);
    },
    [isLockedByMe],
  );

  const handleSaveAppendixRevision = opts => {
    saveAppendixRevision({
      ...opts,
      success: () => addToast('Changes saved successfully'),
      failure: error => {
        addToastAlert({
          severity: 'danger',
          title: 'Failed to save changes',
          message:
            error.statusCode === 403 && isLocked && !isLockedByMe
              ? 'This revision is currently locked by another user'
              : error.message,
        });
      },
    });
  };

  const handleDeleteAppendixRevision = () => {
    if (!isRevisionDraft) {
      addToastAlert({
        severity: 'warning',
        title: 'Cannot delete pending revisions',
        message:
          'Please wait for pending revision to be resolved before deleting the revision.',
      });
    } else {
      openConfirm({
        title: 'Delete Appendix Revision',
        body: (
          <div>
            <span className="d-block mb-3">
              Are you sure you want to delete this revision?{' '}
              {isAppendixDraft &&
                'This appendix has not yet been published. Deleting this revisision is the same as deleting the appendix.'}
            </span>
            <em>
              This action is immediate, does not go through an approval process,
              and cannot be undone.
            </em>
          </div>
        ),
        actionName: 'Delete',
        ok: () =>
          deleteAppendixRevision({
            submissionId: revisionId,
            cancel: doesRevisionHaveChildren,
            refetchItem: !isAppendixDraft,
            success: submission => {
              navigate(isAppendixDraft ? pathToAppendices : pathToAppendix);
              addToast('Revision successfully deleted');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to delete revision',
                message:
                  error.statusCode === 403 && isLocked && !isLockedByMe
                    ? 'This revision is currently locked by another user'
                    : error.message,
              });
            },
          }),
      });
    }
  };

  return (
    <div className="page-container">
      {lockingEnabled && (
        <LockController
          revisionId={revisionId}
          entityId={appendixId}
          revision={revision}
          isLocked={isLocked}
          isLockedByMe={isLockedByMe}
          hasBeenLockedByMe={hasBeenLockedByMe}
          lockTimeLeft={lockTimeLeft}
          lockRevision={lockAppendixRevision}
          fetchRevision={fetchAppendixRevision}
        />
      )}
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={[
            appendix && appendix.values['Name'],
            serviceId && servicesNameMap.get(serviceId),
            portfolioId && portfoliosNameMap.get(portfolioId),
            'Catalog Management',
          ]}
          suptitle="APPENDIX"
          title={
            <I18n
              render={translate => (
                <>
                  {appendix && translate(appendix.values['Name'])}
                  {revision && (
                    <span className="h4">
                      {appendix && ` \u00B7 `}
                      {translate('Revision')} {revision.handle}
                    </span>
                  )}
                </>
              )}
            />
          }
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            {
              label: 'Catalog Management',
              to: `${appLocation}/settings/catalog/appendices`,
            },
            portfolioId && {
              label: portfoliosNameMap.get(portfolioId) || portfolioId,
              to: `${appLocation}/settings/catalog/portfolios/${portfolioId}`,
            },
            serviceId && {
              label: servicesNameMap.get(serviceId) || serviceId,
              to: `${appLocation}/settings${
                portfolioId ? `/portfolios/${portfolioId}` : ''
              }/catalog/services/${serviceId}`,
            },
            appendix && {
              label: appendix.values['Name'],
              to: pathToAppendix,
            },
          ]}
          actions={
            !revisionError &&
            revision && [
              {
                icon: !revisionLoading ? 'sync' : 'sync fa-spin',
                aria: 'Reload Revision',
                onClick: () => fetchAppendixRevision(revisionId, appendixId),
              },
              // Delete revision action
              !isRevisionCompleted &&
                (profile.isSMO || (isOwner && !isRevisionDraft)) && {
                  label: 'Delete Revision',
                  onClick: handleDeleteAppendixRevision,
                  disabled: !isLockedByMe || actionsPending,
                  className: 'text-danger',
                  menu: true,
                },
            ]
          }
        />

        {revisionError ? (
          <ErrorMessage
            title="Failed to retrieve appendix revision."
            message={revisionError.message}
          />
        ) : !revision && revisionLoading ? (
          <LoadingMessage />
        ) : (
          !!revision && (
            <>
              <div className="cards">
                <Card bar={true} barSize="xs" barColor="primary">
                  <CardCol>
                    <CardRow type="title">
                      <span>
                        <I18n>
                          {contentMode === CONTENT_MANAGER_MODE.EDIT
                            ? 'Edit Revision'
                            : contentMode === CONTENT_MANAGER_MODE.SUBMIT
                              ? 'Review and Submit Revision'
                              : revision.values[C.FIELD_STATUS] === 'Pending'
                                ? 'Revision Pending Approval'
                                : 'Review Revision'}
                        </I18n>
                      </span>
                      <div className="ml-auto">
                        {contentMode === CONTENT_MANAGER_MODE.REVIEW && (
                          <Link
                            className="btn btn-outline-dark"
                            to={
                              isRevisionCompleted
                                ? pathToAppendix
                                : pathToRevision
                            }
                          >
                            <span className="fa fa-arrow-left fa-fw" />
                            <I18n>Return to Appendix</I18n>
                          </Link>
                        )}
                        {contentMode === CONTENT_MANAGER_MODE.EDIT && (
                          <>
                            <button
                              className="btn btn-outline-dark"
                              onClick={() => {
                                if (!isDirty) {
                                  navigate(pathToRevision);
                                } else {
                                  openConfirm({
                                    title: 'Unsaved Changes',
                                    body:
                                      'You have unsaved changes that will be lost if you continue. Are you sure you want to exit edit mode?',
                                    actionName: 'Exit Edit Mode',
                                    ok: () => navigate(pathToRevision),
                                  });
                                }
                              }}
                            >
                              <span className="fa fa-arrow-left fa-fw" />
                              <I18n>Exit Edit Mode</I18n>
                            </button>
                            <button
                              className="btn btn-primary ml-2"
                              onClick={() => navigate('submit')}
                              disabled={isDirty || !isLockedByMe}
                            >
                              <I18n>Review and Submit</I18n>
                            </button>
                          </>
                        )}
                        {contentMode === CONTENT_MANAGER_MODE.SUBMIT && (
                          <>
                            <Link className="btn btn-outline-dark" to="..">
                              <span className="fa fa-arrow-left fa-fw" />
                              <I18n>Continue Editing</I18n>
                            </Link>
                            <button
                              className="btn btn-primary ml-2"
                              onClick={() =>
                                submitAppendixRevision({
                                  submissionId: revision.id,
                                  // Use updatedAt as a pseudo version id to
                                  // ensure we're submitting the latest version
                                  updatedAt: revision.updatedAt,
                                  success: submission => {
                                    navigate(pathToRevision);
                                    addToast(
                                      'Revision successfully submitted for approval',
                                    );
                                  },
                                  failure: error => {
                                    addToastAlert({
                                      severity: 'danger',
                                      title: 'Failed to submit revision',
                                      message: error.message,
                                    });
                                  },
                                })
                              }
                              disabled={!isLockedByMe}
                            >
                              <I18n>Submit for Approval</I18n>
                            </button>
                          </>
                        )}
                      </div>
                    </CardRow>
                    <CardRow className="text-muted" type="meta">
                      <dl className="flat align-items-center">
                        <div>
                          <dd>
                            <span
                              className={`badge badge-${getStatusColor(
                                revision.values['Status'],
                              )} badge-stylized text-muted`}
                            >
                              <I18n>{revision.values['Status']}</I18n>
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt>
                            <I18n>Created</I18n>
                          </dt>
                          <dd>
                            <TimeAgo timestamp={revision.createdAt} />
                          </dd>
                        </div>
                        <div>
                          <dt>
                            <I18n>Updated</I18n>
                          </dt>
                          <dd>
                            <TimeAgo timestamp={revision.updatedAt} />
                          </dd>
                        </div>
                      </dl>
                    </CardRow>
                    {lockingEnabled && (
                      <CardRow className="mt-2">
                        {isLockedByMe ? (
                          <>
                            <span className="badge badge-success badge-pill">
                              <span className="fa fa-lock mr-1" />
                              <I18n>Locked By You</I18n>
                              {' \u00B7 '}
                              <Countdown
                                date={new Date().getTime() + lockTimeLeft}
                              />
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                lockAppendixRevision({
                                  submissionId: revision.id,
                                })
                              }
                            >
                              Renew Lock
                            </button>
                          </>
                        ) : isLocked ? (
                          <>
                            <span className="badge badge-danger badge-pill">
                              <span className="fa fa-lock mr-1" />
                              <I18n>Locked By Another User</I18n>
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                fetchAppendixRevision(revisionId, appendixId)
                              }
                            >
                              Refresh
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="badge badge-subtle badge-pill">
                              <span className="fa fa-lock-open mr-1" />
                              <I18n>
                                {hasBeenLockedByMe
                                  ? 'Lock Expired'
                                  : 'Not Locked'}
                              </I18n>
                            </span>
                            <button
                              className="btn btn-text ml-2"
                              onClick={() =>
                                lockAppendixRevision({
                                  submissionId: revision.id,
                                })
                              }
                            >
                              Obtain Lock
                            </button>
                          </>
                        )}
                      </CardRow>
                    )}
                  </CardCol>
                </Card>
              </div>

              <ContentManager
                submission={revision}
                save={handleSaveAppendixRevision}
                actionsPending={actionsPending}
                imageDir={`Appendix/${revision.values[C.FIELD_APPENDIX_ID]}`}
                username={profile.username}
                // If in EDIT mode but the user never had a lock, show in review
                // mode. If user did have a lock at any point, show in EDIT mode
                // to prevent changes from getting lost when the lock is lost.
                mode={
                  contentMode === CONTENT_MANAGER_MODE.EDIT &&
                  !hasBeenLockedByMe
                    ? CONTENT_MANAGER_MODE.REVIEW
                    : contentMode
                }
                // Provide a function to be called when the dirty flag changes
                // so that we can use the dirty state in this component.
                onDirtyChange={setIsDirty}
                indexSections={true}
                enableSearch={true}
              />
            </>
          )
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const appendices =
    state.settingsAppendix.data.get(props.appendixId) || List();
  const appendix =
    state.settingsAppendix.current &&
    state.settingsAppendix.current.values[C.FIELD_APPENDIX_ID] ===
      props.appendixId
      ? state.settingsAppendix.current
      : appendices.first();
  return {
    // State for selected revision
    revision: state.settingsAppendix.revision,
    revisionLoading: state.settingsAppendix.revisionLoading,
    revisionError: state.settingsAppendix.revisionError,
    // State for current appendix
    appendix,
    appendixContentReady: appendix && appendix.values['Content'],
    appendixLoading: state.settingsAppendix.currentLoading,
    appendixError: state.settingsAppendix.currentError,
    // State showing if any user actions are pending
    actionsPending: state.settingsAppendix.actionsPending > 0,
    // All portfolios
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // All services
    servicesNameMap: state.settingsService.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_SERVICE_ID], p.values['Name']),
      Map(),
    ),
    // Other state
    profile: state.app.profile,
    isOwner: selectIsAppendixOwner(state.app.profile),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchAppendixRevision: actions.fetchAppendixRevisionRequest,
  saveAppendixRevision: actions.saveAppendixRevisionRequest,
  deleteAppendixRevision: actions.deleteAppendixRevisionRequest,
  submitAppendixRevision: actions.submitAppendixRevisionRequest,
  lockAppendixRevision: actions.lockAppendixRevisionRequest,
  resetAppendixRevision: actions.resetAppendixRevisionRequest,
};

export const AppendixRevision = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppendixRevisionComponent);
